import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Clock, Mail, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Reveal, PageHero, useSeo } from "../components/Shared";
import { EMAIL, HOURS, LOCATION } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useSeo(
    "Contact | Dishan International Group",
    "Book a consultation with Dishan International Group. Tell us what you are working through, we'll help you find the right place to begin."
  );
  const [sent, setSent] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/consultations`, Object.fromEntries(new FormData(e.currentTarget)));
      setSent(true);
      setAiSummary(res.data.ai_summary || "");
      toast.success("Your consultation request is on its way.");
      e.currentTarget.reset();
    } catch {
      toast.error(`We couldn't send that just now. Please email ${EMAIL}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleKey="contact.hero.title" title="Let's make the next move useful."
        subtitleKey="contact.hero.subtitle" subtitle="Tell us what you are working through. We'll help you find the most relevant place to begin."
      />
      <main>
        <section className="section">
          <div className="container form-layout">
            <Reveal>
              <span className="eyebrow">Start a conversation</span>
              <h2>Your context is the brief.</h2>
              <p>Whether you are building a business, a brand, a career or a more sustainable way of working, share a little about where you are.</p>
              <div className="contact-details">
                <a href={`mailto:${EMAIL}`} data-testid="contact-email-link"><Mail size={16} /> {EMAIL}</a>
                <span><Clock size={16} /> {HOURS}</span>
                <span><MapPin size={16} /> {LOCATION}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <form className="form-card" onSubmit={submit} data-testid="consultation-form">
                <h3>Book a consultation</h3>
                <div className="form-row">
                  <label>Name<input name="name" required placeholder="Your name" data-testid="contact-name-input" /></label>
                  <label>Email<input name="email" type="email" required placeholder="you@company.com" data-testid="contact-email-input" /></label>
                </div>
                <div className="form-row">
                  <label>Phone<input name="phone" placeholder="+91" data-testid="contact-phone-input" /></label>
                  <label>Organization<input name="organization" placeholder="Company or institution" data-testid="contact-organization-input" /></label>
                </div>
                <label>What can we help with?
                  <select name="requirement_type" required data-testid="contact-requirement-select">
                    <option value="">Select an area</option>
                    <option>Business & brand consulting</option>
                    <option>Education & career</option>
                    <option>Corporate solutions</option>
                    <option>Wellness support</option>
                    <option>Partnerships & ventures</option>
                  </select>
                </label>
                <label>Message<textarea name="message" required rows={4} placeholder="Tell us what you are working through..." data-testid="contact-message-input" /></label>
                <label>Preferred contact method<input name="preferred_contact" placeholder="Email, phone or WhatsApp" data-testid="contact-preferred-input" /></label>
                <button className="btn btn-primary form-submit" type="submit" disabled={loading} data-testid="contact-submit-button">
                  {loading ? "Sending…" : "Send Enquiry"} <ArrowUpRight size={16} />
                </button>
                {sent && <p className="success-message" data-testid="contact-success-message">Thank you, our team will be in touch shortly.</p>}
                {sent && aiSummary && (
                  <div className="ai-brief" data-testid="contact-ai-summary">
                    <span className="ai-brief-label"><Sparkles size={14} /> Smart intake · the brief our team received</span>
                    <p>{aiSummary}</p>
                  </div>
                )}
              </form>
            </Reveal>
          </div>
        </section>
      </main>
    </>
  );
}
